// Standalone-only status-based redirect; mirrors EncounterRedirect (connected).

import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { appointmentWrapperService } from '@/services/appointmentWrapper';
import { computeAppointmentUrl } from '@/utils/appointmentUrl';
import { Loader2, AlertCircle } from 'lucide-react';

const AppointmentRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  useEffect(() => {
    const determineRedirect = async () => {
      if (!id) {
        setError('No appointment ID provided');
        setLoading(false);
        return;
      }

      try {
        const appointment = await appointmentWrapperService.getAppointment(id);
        const url = computeAppointmentUrl(appointment);
        setRedirectUrl(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load appointment');
      } finally {
        setLoading(false);
      }
    };

    determineRedirect();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading appointment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">
            Unable to Load Appointment
          </h1>
          <p className="mt-2 text-gray-600">{error}</p>
          <a
            href="/dashboard"
            className="mt-6 inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (redirectUrl) {
    return <Navigate to={redirectUrl} replace />;
  }

  return null;
};

export default AppointmentRedirect;
